import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Building2,
  Layers,
  BarChart3,
  Landmark,
  History,
  Database,
  DollarSign,
  TrendingUp,
  Sliders,
  Newspaper,
  BookOpen,
  FileText,
  BarChart2,
  Users,
  Zap,
  AlertTriangle,
  RefreshCw,
  Loader2,
  XCircle,
} from 'lucide-react';
import { api, standaloneCompaniesAPI } from '../lib/api';
import { Case, BusinessModelCanvas as BusinessModelCanvasType } from '../types';
import Spinner from '../components/ui/Spinner';
import Card, { CardBody, CardHeader } from '../components/ui/Card';
import Button from '../components/ui/Button';
import LinkedResearchSection from '../components/caseDetail/LinkedResearchSection';
import Tooltip from '../components/ui/Tooltip';
import IndustryBenchmarksPanel from '../components/case-analytics/IndustryBenchmarksPanel';
import FocalCompanyFinancials from '../components/case-analytics/FocalCompanyFinancials';
import EVAnalysisPanel from '../components/case-analytics/EVAnalysisPanel';
import IndustryVersionTimeline from '../components/case-analytics/IndustryVersionTimeline';
import IndustryResearchAgeBadge from '../components/case-analytics/IndustryResearchAgeBadge';
import PhaseBreadcrumb from '../components/case-analytics/PhaseBreadcrumb';
import SchemaDDLViewer from '../components/case-analytics/SchemaDDLViewer';
import IndustryResearchPanel, { IndustryPanelTab } from '../components/case-analytics/IndustryResearchPanel';
import { NewsIntelligenceTab } from '../components/company-research/news-intelligence';
import { DocumentInsightsTab } from '../components/company-research/document-insights';
import CompanyFilingsTab from '../components/financial/CompanyFilingsTab';
import BusinessModelCanvas from '../components/visualizations/BusinessModelCanvas';

interface CompanyData {
  company: {
    companyId: string;
    name: string;
    websiteUrl: string;
    researchDate: string;
    confidenceScore: number;
  };
  financialProfile?: {
    revenue?: string;
    revenuePeriod?: string;
    margins?: string;
    revenueSegments?: any[];
  };
  businessStructure?: {
    businessUnits?: string[];
    productLines?: string[];
    geographicRegions?: string[];
  };
  operationalProfile?: {
    manufacturing?: string;
    supplyChain?: string;
    distributionChannels?: string[];
    customerSegments?: string[];
  };
  organizationalScale?: {
    employeeCount?: string;
    facilities?: string;
    customerBase?: string;
    supplierBase?: string;
  };
  industryContext?: {
    vertical?: string;
    subSector?: string;
    regulations?: string[];
    terminology?: string[];
  };
  strategicInitiatives?: {
    growthInitiatives?: string[];
    transformationPrograms?: string[];
    challenges?: string[];
    maActivity?: string[];
  };
  dataComplexity?: {
    scope?: string;
    multiChannel?: boolean;
    productComplexity?: string;
    recentAcquisitions?: string[];
  };
  sources?: string[];
}

interface CaseContextEnvelope {
  caseId: string;
  company?: { companyId?: string | null; name?: string | null } | null;
  industry?: { industryId?: string | null; name?: string | null } | null;
}

// ── Section & tab types ───────────────────────────────────────────────────────

type SectionKey = 'company' | 'industry' | 'schema';

type CompanyTabKey =
  | 'overview'
  | 'financials'
  | 'ev'
  | 'revenue'
  | 'sensitivity'
  | 'news'
  | 'docInsights'
  | 'filings'
  | 'businessModel';

type IndustryTabKey =
  | 'industryOverview'
  | 'market'
  | 'competitive'
  | 'technology'
  | 'disruptions'
  | 'benchmarks'
  | 'versions';

// ── Helpers ───────────────────────────────────────────────────────────────────

function inferCountryFromCompanyId(companyId: string | null | undefined): string | undefined {
  if (!companyId) return undefined;
  if (companyId.startsWith('uk_') || companyId.startsWith('uk-')) return 'UK';
  if (companyId.startsWith('ind_') || companyId.startsWith('ind-')) return 'INDIA';
  if (companyId.startsWith('us_') || companyId.startsWith('us-')) return 'US';
  return undefined;
}

// ─────────────────────────────────────────────────────────────────────────────

export default function CaseDetail() {
  const { caseId } = useParams<{ caseId: string }>();

  // Section + sub-tab state
  const [activeSection, setActiveSection] = useState<SectionKey>('company');
  const [activeCompanyTab, setActiveCompanyTab] = useState<CompanyTabKey>('overview');
  const [activeIndustryTab, setActiveIndustryTab] = useState<IndustryTabKey>('industryOverview');

  // ── Queries ──────────────────────────────────────────────────────────────
  const { data: project, isLoading: projectLoading } = useQuery<Case>({
    queryKey: ['case', caseId],
    queryFn: () => api.get<Case>(`/cases/${caseId}`),
    enabled: !!caseId,
  });

  const { data: companyData, isLoading: companyLoading } = useQuery<CompanyData>({
    queryKey: ['company', caseId],
    queryFn: () => api.get<CompanyData>(`/company-research/cases/${caseId}/company`),
    enabled: !!caseId,
    retry: false,
  });

  const { data: caseContext } = useQuery<CaseContextEnvelope>({
    queryKey: ['case-context', 'envelope', caseId],
    queryFn: () => api.get<CaseContextEnvelope>(`/case-discovery/case-context/${caseId}`),
    enabled: !!caseId,
    staleTime: 1000 * 60 * 5,
    retry: false,
  });

  const industryId = caseContext?.industry?.industryId ?? null;
  const linkedCompanyId = caseContext?.company?.companyId ?? null;

  // Derive company metadata needed for conditional tab rendering
  const resolvedCompanyId = companyData?.company?.companyId ?? linkedCompanyId;
  const companyName = companyData?.company?.name ?? null;
  const companyCountry = inferCountryFromCompanyId(resolvedCompanyId) ?? 'INDIA';
  const isUSCompany = companyCountry === 'US';
  const isUKCompany = companyCountry === 'UK';
  const isIndiaCompany = companyCountry === 'INDIA';
  const isNonUSCompany = isIndiaCompany || isUKCompany;

  // Business Model Canvas — fetched on demand when its tab becomes active.
  // Uses react-query so it participates in the shared cache/de-duplication and
  // respects staleTime (the previous manual effect listed its own state in the
  // dependency array and re-ran on every state transition).
  const businessModelTabActive =
    activeSection === 'company' && activeCompanyTab === 'businessModel';
  const {
    data: businessModel = null,
    isLoading: bmLoading,
    error: bmErrorRaw,
    refetch: refetchBusinessModel,
  } = useQuery<BusinessModelCanvasType>({
    queryKey: ['business-model', resolvedCompanyId],
    queryFn: () => standaloneCompaniesAPI.getBusinessModel(resolvedCompanyId as string),
    enabled: businessModelTabActive && !!resolvedCompanyId,
    retry: false,
  });
  const bmError = bmErrorRaw
    ? (bmErrorRaw as any)?.response?.data?.detail ||
      'Failed to load business model. Requires SEC filing data to be processed.'
    : null;

  const isLoading = projectLoading || companyLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!project) {
    return (
      <Card>
        <CardBody>
          <p className="text-center text-neutral-600">Case not found</p>
        </CardBody>
      </Card>
    );
  }

  // ── Company sub-tabs (conditionally include country-specific ones) ─────────
  const companyTabs: Array<{
    key: CompanyTabKey;
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    requires?: 'company';
    tooltip?: string;
  }> = [
    { key: 'overview',      label: 'Overview',          icon: Building2 },
    { key: 'financials',    label: 'Financials',         icon: DollarSign,  requires: 'company' },
    { key: 'ev',            label: 'EV',                 icon: Landmark,    requires: 'company' },
    { key: 'revenue',       label: 'Revenue & Capital',  icon: TrendingUp,  requires: 'company' },
    { key: 'sensitivity',   label: 'Sensitivity',        icon: Sliders,     requires: 'company' },
    { key: 'news',          label: 'News Intelligence',  icon: Newspaper,   requires: 'company' },
    ...(isIndiaCompany
      ? [{ key: 'docInsights' as CompanyTabKey, label: 'Document Insights', icon: BookOpen, requires: 'company' as const }]
      : []),
    ...(isNonUSCompany && resolvedCompanyId
      ? [{ key: 'filings' as CompanyTabKey, label: 'Filings & Reports', icon: FileText, requires: 'company' as const }]
      : []),
    ...(isUSCompany && resolvedCompanyId
      ? [{ key: 'businessModel' as CompanyTabKey, label: 'Business Model', icon: Layers, requires: 'company' as const }]
      : []),
  ];

  const industryTabs: Array<{
    key: IndustryTabKey;
    label: string;
    icon: React.ComponentType<{ className?: string }>;
  }> = [
    { key: 'industryOverview', label: 'Overview',            icon: TrendingUp },
    { key: 'market',           label: 'Market Analysis',     icon: BarChart3 },
    { key: 'competitive',      label: 'Competitive',         icon: Users },
    { key: 'technology',       label: 'Technology',          icon: Zap },
    { key: 'disruptions',      label: 'Disruptions & Risks', icon: AlertTriangle },
    { key: 'benchmarks',       label: 'Benchmarks',          icon: BarChart2 },
    { key: 'versions',         label: 'Versions',            icon: History },
  ];

  const hasCompany = !!(resolvedCompanyId || companyData);
  const hasIndustry = !!industryId;

  const companyTabDisabledReason = (key: CompanyTabKey): string | null => {
    if (key === 'overview') return null;
    if (!hasCompany) return 'Link a Company Research record to this case to enable this tab.';
    return null;
  };

  return (
    <div className="max-w-screen-2xl mx-auto">
      <h1 className="text-[40px] font-bold text-neutral-900 mb-2">{project.name}</h1>
      {project.description && (
        <p className="text-neutral-600 mb-6">{project.description}</p>
      )}

      {/* ── Phase breadcrumb ─────────────────────────────────────────────────
          Shows Discovery → Analysis → Design with a "Continue to {next phase}"
          CTA so users can hop into the next pending wizard without going back
          to the Case Library. */}
      <PhaseBreadcrumb
        caseId={caseId}
        currentPhase={project.currentPhase}
        discoveryPhaseStatus={project.discoveryPhaseStatus}
        analysisPhaseStatus={project.analysisPhaseStatus}
        designPhaseStatus={project.designPhaseStatus}
      />

      {/* ── Top-level section switcher ────────────────────────────────────── */}
      <div className="flex items-center gap-2 mb-1">
        {(
          [
            { key: 'company'  as SectionKey, label: 'Company Research',  icon: Building2 },
            { key: 'industry' as SectionKey, label: 'Industry Research', icon: BarChart3 },
            { key: 'schema'   as SectionKey, label: 'Schema',            icon: Database  },
          ] as const
        ).map(({ key, label, icon: Icon }) => {
          const active = activeSection === key;
          const disabled =
            (key === 'industry' && !hasIndustry);

          const btn = (
            <button
              key={key}
              onClick={() => !disabled && setActiveSection(key)}
              disabled={disabled}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-colors border ${
                active
                  ? 'bg-primary-600 text-white border-primary-600 shadow-sm'
                  : disabled
                  ? 'border-neutral-200 text-neutral-300 cursor-not-allowed bg-white'
                  : 'border-neutral-200 text-neutral-700 hover:border-primary-300 hover:text-primary-700 bg-white'
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          );

          if (disabled) {
            return (
              <Tooltip key={key} content="Link an Industry Research record to this case to enable this section." position="bottom">
                {btn}
              </Tooltip>
            );
          }
          return btn;
        })}
      </div>

      {/* ── Sub-tab bar (Company or Industry) ────────────────────────────── */}
      {activeSection === 'company' && (
        <div className="flex gap-1 border-b border-neutral-200 mb-6 overflow-x-auto mt-4">
          {companyTabs.map((tab) => {
            const Icon = tab.icon;
            const disabledReason = companyTabDisabledReason(tab.key);
            const active = activeCompanyTab === tab.key;
            const disabled = !!disabledReason;
            const btn = (
              <button
                key={tab.key}
                onClick={() => !disabled && setActiveCompanyTab(tab.key)}
                disabled={disabled}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors whitespace-nowrap text-sm ${
                  active
                    ? 'border-primary-600 text-primary-600 font-medium'
                    : disabled
                    ? 'border-transparent text-neutral-300 cursor-not-allowed'
                    : 'border-transparent text-neutral-600 hover:text-neutral-900'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
            if (!disabledReason) return btn;
            return (
              <Tooltip key={tab.key} content={disabledReason} position="bottom">
                {btn}
              </Tooltip>
            );
          })}
        </div>
      )}

      {activeSection === 'industry' && (
        <div className="flex gap-1 border-b border-neutral-200 mb-6 overflow-x-auto mt-4">
          {industryTabs.map((tab) => {
            const Icon = tab.icon;
            const active = activeIndustryTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveIndustryTab(tab.key)}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors whitespace-nowrap text-sm ${
                  active
                    ? 'border-primary-600 text-primary-600 font-medium'
                    : 'border-transparent text-neutral-600 hover:text-neutral-900'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      )}

      {activeSection === 'schema' && <div className="mb-6" />}

      {/* ── Tab content ───────────────────────────────────────────────────── */}

      {/* Company Research content */}
      {activeSection === 'company' && activeCompanyTab === 'overview' && (
        <OverviewTab companyData={companyData} caseId={caseId} />
      )}
      {activeSection === 'company' && activeCompanyTab === 'financials' && (
        <FocalCompanyFinancials companyName={companyName} country={companyCountry} mode="full" />
      )}
      {activeSection === 'company' && activeCompanyTab === 'ev' && (
        <EVAnalysisPanel companyName={companyName} country={companyCountry} mode="full" />
      )}
      {activeSection === 'company' && activeCompanyTab === 'revenue' && (
        <FocalCompanyFinancials companyName={companyName} country={companyCountry} section="revenue" />
      )}
      {activeSection === 'company' && activeCompanyTab === 'sensitivity' && (
        <FocalCompanyFinancials companyName={companyName} country={companyCountry} section="sensitivity" />
      )}
      {activeSection === 'company' && activeCompanyTab === 'news' && companyName && (
        <NewsIntelligenceTab companyName={companyName} />
      )}
      {activeSection === 'company' && activeCompanyTab === 'docInsights' && companyName && (
        <DocumentInsightsTab companyName={companyName} country={companyCountry.toLowerCase()} />
      )}
      {activeSection === 'company' && activeCompanyTab === 'filings' && resolvedCompanyId && companyName && (
        <CompanyFilingsTab
          companyId={resolvedCompanyId}
          companyCountry={companyCountry as 'INDIA' | 'UK'}
          companyName={companyName}
          regionConfig={null}
        />
      )}
      {activeSection === 'company' && activeCompanyTab === 'businessModel' && (
        <BusinessModelSection
          loading={bmLoading}
          error={bmError}
          canvas={businessModel}
          companyName={companyName}
          onRefresh={() => {
            void refetchBusinessModel();
          }}
        />
      )}

      {/* Industry Research content */}
      {activeSection === 'industry' && industryId && (
        <>
          {/* Freshness badge — visible across every Industry sub-tab so the
              consultant always knows whether the underlying research is current.
              Self-hides when no industry is linked or no refresh timestamp
              exists, so it never flashes an empty pill. */}
          <div className="flex justify-end mb-3">
            <IndustryResearchAgeBadge industryId={industryId} />
          </div>
          {activeIndustryTab === 'industryOverview' && (
            <IndustryResearchPanel industryId={industryId} tab="overview" />
          )}
          {activeIndustryTab === 'market' && (
            <IndustryResearchPanel industryId={industryId} tab="market" />
          )}
          {activeIndustryTab === 'competitive' && (
            <IndustryResearchPanel industryId={industryId} tab="competitive" />
          )}
          {activeIndustryTab === 'technology' && (
            <IndustryResearchPanel industryId={industryId} tab="technology" />
          )}
          {activeIndustryTab === 'disruptions' && (
            <IndustryResearchPanel industryId={industryId} tab="disruptions" />
          )}
          {activeIndustryTab === 'benchmarks' && (
            <IndustryBenchmarksPanel industryId={industryId} mode="full" />
          )}
          {activeIndustryTab === 'versions' && (
            <IndustryVersionTimeline industryId={industryId} />
          )}
        </>
      )}

      {/* Schema */}
      {activeSection === 'schema' && <SchemaDDLViewer caseId={caseId} />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Business Model section — on-demand fetch + loading / error / empty states
// ─────────────────────────────────────────────────────────────────────────────

function BusinessModelSection({
  loading,
  error,
  canvas,
  companyName,
  onRefresh,
}: {
  loading: boolean;
  error: string | null;
  canvas: BusinessModelCanvasType | null;
  companyName: string | null;
  onRefresh: () => void;
}) {
  if (loading) {
    return (
      <Card>
        <CardBody className="text-center py-12">
          <Loader2 className="h-10 w-10 text-primary-600 mx-auto mb-3 animate-spin" />
          <p className="text-neutral-600">Loading Business Model Canvas…</p>
        </CardBody>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <h2 className="text-xl font-semibold text-neutral-900">Business Model Canvas</h2>
        </CardHeader>
        <CardBody>
          <div className="text-center py-10">
            <XCircle className="h-10 w-10 text-red-500 mx-auto mb-3" />
            <p className="text-red-600 font-medium mb-1">Failed to load Business Model</p>
            <p className="text-sm text-neutral-600 max-w-md mx-auto mb-4">{error}</p>
            <Button variant="outline" size="sm" onClick={onRefresh}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </div>
        </CardBody>
      </Card>
    );
  }

  if (canvas) {
    return <BusinessModelCanvas canvas={canvas} companyName={companyName ?? undefined} />;
  }

  return (
    <Card>
      <CardBody className="text-center py-10 text-neutral-500">
        <Layers className="h-10 w-10 text-neutral-300 mx-auto mb-3" />
        <p className="font-medium">No Business Model Data Available</p>
        <p className="text-sm mt-1 max-w-md mx-auto">
          Business model extraction requires SEC filing data to be processed for this company.
        </p>
      </CardBody>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Overview tab — original single-pane company research content
// ─────────────────────────────────────────────────────────────────────────────

function OverviewTab({
  companyData,
  caseId,
}: {
  companyData: CompanyData | undefined;
  caseId: string | undefined;
}) {
  const extractValue = (data: any): string => {
    if (typeof data === 'string') return data;
    if (data?.value) return data.value;
    return JSON.stringify(data);
  };

  return (
    <div>
      {companyData ? (
        <div className="space-y-6">
          {/* Company Header */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <Building2 className="w-6 h-6 text-primary-600" />
                <div>
                  <h2 className="text-2xl font-bold text-neutral-900">
                    {companyData.company.name}
                  </h2>
                  {companyData.company.websiteUrl && (
                    <a
                      href={companyData.company.websiteUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:text-primary-700 text-sm"
                    >
                      {companyData.company.websiteUrl}
                    </a>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardBody>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="font-medium text-neutral-600">Company ID:</span>
                  <p className="text-neutral-900">{companyData.company.companyId}</p>
                </div>
                <div>
                  <span className="font-medium text-neutral-600">Research Date:</span>
                  <p className="text-neutral-900">
                    {new Date(companyData.company.researchDate).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <span className="font-medium text-neutral-600">Confidence Score:</span>
                  <p className="text-neutral-900">{companyData.company.confidenceScore}%</p>
                </div>
              </div>
            </CardBody>
          </Card>

          {companyData.financialProfile && (
            <Card>
              <CardHeader>
                <h3 className="text-lg font-semibold text-neutral-900">💰 Financial Profile</h3>
              </CardHeader>
              <CardBody>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm font-medium text-neutral-600">Revenue:</span>
                      <p className="text-neutral-900 font-medium">
                        {extractValue(companyData.financialProfile.revenue)}
                      </p>
                      {companyData.financialProfile.revenuePeriod && (
                        <p className="text-sm text-neutral-500">{companyData.financialProfile.revenuePeriod}</p>
                      )}
                    </div>
                    <div>
                      <span className="text-sm font-medium text-neutral-600">Margins:</span>
                      <p className="text-neutral-900">{extractValue(companyData.financialProfile.margins)}</p>
                    </div>
                  </div>
                  {companyData.financialProfile.revenueSegments &&
                    companyData.financialProfile.revenueSegments.length > 0 && (
                      <div>
                        <span className="text-sm font-medium text-neutral-600">Revenue Segments:</span>
                        <ul className="list-disc list-inside text-neutral-900 mt-2 space-y-1">
                          {companyData.financialProfile.revenueSegments.map((seg: any, idx: number) => (
                            <li key={idx} className="text-sm">
                              {typeof seg === 'string' ? seg : `${seg.segment}: ${seg.percentage}%`}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                </div>
              </CardBody>
            </Card>
          )}

          {companyData.businessStructure && (
            <Card>
              <CardHeader>
                <h3 className="text-lg font-semibold text-neutral-900">🏢 Business Structure</h3>
              </CardHeader>
              <CardBody>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {companyData.businessStructure.businessUnits?.length ? (
                    <div>
                      <span className="text-sm font-medium text-neutral-600">Business Units:</span>
                      <ul className="list-disc list-inside text-neutral-900 mt-2 space-y-1">
                        {companyData.businessStructure.businessUnits.map((unit, idx) => (
                          <li key={idx} className="text-sm">{unit}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                  {companyData.businessStructure.productLines?.length ? (
                    <div>
                      <span className="text-sm font-medium text-neutral-600">Product Lines:</span>
                      <ul className="list-disc list-inside text-neutral-900 mt-2 space-y-1">
                        {companyData.businessStructure.productLines.map((p, idx) => (
                          <li key={idx} className="text-sm">{p}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                  {companyData.businessStructure.geographicRegions?.length ? (
                    <div>
                      <span className="text-sm font-medium text-neutral-600">Geographic Regions:</span>
                      <ul className="list-disc list-inside text-neutral-900 mt-2 space-y-1">
                        {companyData.businessStructure.geographicRegions.map((r, idx) => (
                          <li key={idx} className="text-sm">{r}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </div>
              </CardBody>
            </Card>
          )}

          {companyData.operationalProfile && (
            <Card>
              <CardHeader>
                <h3 className="text-lg font-semibold text-neutral-900">⚙️ Operational Profile</h3>
              </CardHeader>
              <CardBody>
                <div className="space-y-4">
                  {companyData.operationalProfile.manufacturing && (
                    <div>
                      <span className="text-sm font-medium text-neutral-600">Manufacturing:</span>
                      <p className="text-neutral-900 text-sm mt-1">{companyData.operationalProfile.manufacturing}</p>
                    </div>
                  )}
                  {companyData.operationalProfile.supplyChain && (
                    <div>
                      <span className="text-sm font-medium text-neutral-600">Supply Chain:</span>
                      <p className="text-neutral-900 text-sm mt-1">{companyData.operationalProfile.supplyChain}</p>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-6">
                    {companyData.operationalProfile.distributionChannels?.length ? (
                      <div>
                        <span className="text-sm font-medium text-neutral-600">Distribution Channels:</span>
                        <ul className="list-disc list-inside text-neutral-900 mt-2 space-y-1">
                          {companyData.operationalProfile.distributionChannels.map((c, idx) => (
                            <li key={idx} className="text-sm">{c}</li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                    {companyData.operationalProfile.customerSegments?.length ? (
                      <div>
                        <span className="text-sm font-medium text-neutral-600">Customer Segments:</span>
                        <ul className="list-disc list-inside text-neutral-900 mt-2 space-y-1">
                          {companyData.operationalProfile.customerSegments.map((s, idx) => (
                            <li key={idx} className="text-sm">{s}</li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </div>
                </div>
              </CardBody>
            </Card>
          )}

          {companyData.organizationalScale && (
            <Card>
              <CardHeader>
                <h3 className="text-lg font-semibold text-neutral-900">👥 Organizational Scale</h3>
              </CardHeader>
              <CardBody>
                <div className="grid grid-cols-2 gap-4">
                  {companyData.organizationalScale.employeeCount && (
                    <div>
                      <span className="text-sm font-medium text-neutral-600">Employees:</span>
                      <p className="text-neutral-900">{extractValue(companyData.organizationalScale.employeeCount)}</p>
                    </div>
                  )}
                  {companyData.organizationalScale.facilities && (
                    <div>
                      <span className="text-sm font-medium text-neutral-600">Facilities:</span>
                      <p className="text-neutral-900">{extractValue(companyData.organizationalScale.facilities)}</p>
                    </div>
                  )}
                  {companyData.organizationalScale.customerBase && (
                    <div>
                      <span className="text-sm font-medium text-neutral-600">Customer Base:</span>
                      <p className="text-neutral-900">{extractValue(companyData.organizationalScale.customerBase)}</p>
                    </div>
                  )}
                  {companyData.organizationalScale.supplierBase && (
                    <div>
                      <span className="text-sm font-medium text-neutral-600">Supplier Base:</span>
                      <p className="text-neutral-900">{extractValue(companyData.organizationalScale.supplierBase)}</p>
                    </div>
                  )}
                </div>
              </CardBody>
            </Card>
          )}

          {companyData.industryContext && (
            <Card>
              <CardHeader>
                <h3 className="text-lg font-semibold text-neutral-900">🏭 Industry Context</h3>
              </CardHeader>
              <CardBody>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    {companyData.industryContext.vertical && (
                      <div>
                        <span className="text-sm font-medium text-neutral-600">Vertical:</span>
                        <p className="text-neutral-900">{companyData.industryContext.vertical}</p>
                      </div>
                    )}
                    {companyData.industryContext.subSector && (
                      <div>
                        <span className="text-sm font-medium text-neutral-600">Sub-Sector:</span>
                        <p className="text-neutral-900">{companyData.industryContext.subSector}</p>
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-6">
                    {companyData.industryContext.regulations?.length ? (
                      <div>
                        <span className="text-sm font-medium text-neutral-600">Regulations:</span>
                        <ul className="list-disc list-inside text-neutral-900 mt-2 space-y-1">
                          {companyData.industryContext.regulations.map((r, idx) => (
                            <li key={idx} className="text-sm">{r}</li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                    {companyData.industryContext.terminology?.length ? (
                      <div>
                        <span className="text-sm font-medium text-neutral-600">Key Terms:</span>
                        <ul className="list-disc list-inside text-neutral-900 mt-2 space-y-1">
                          {companyData.industryContext.terminology.map((t, idx) => (
                            <li key={idx} className="text-sm">{t}</li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </div>
                </div>
              </CardBody>
            </Card>
          )}

          {companyData.strategicInitiatives && (
            <Card>
              <CardHeader>
                <h3 className="text-lg font-semibold text-neutral-900">🎯 Strategic Initiatives</h3>
              </CardHeader>
              <CardBody>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {companyData.strategicInitiatives.growthInitiatives?.length ? (
                    <div>
                      <span className="text-sm font-medium text-neutral-600">Growth Initiatives:</span>
                      <ul className="list-disc list-inside text-neutral-900 mt-2 space-y-1">
                        {companyData.strategicInitiatives.growthInitiatives.map((i, idx) => (
                          <li key={idx} className="text-sm">{i}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                  {companyData.strategicInitiatives.transformationPrograms?.length ? (
                    <div>
                      <span className="text-sm font-medium text-neutral-600">Transformation Programs:</span>
                      <ul className="list-disc list-inside text-neutral-900 mt-2 space-y-1">
                        {companyData.strategicInitiatives.transformationPrograms.map((p, idx) => (
                          <li key={idx} className="text-sm">{p}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                  {companyData.strategicInitiatives.challenges?.length ? (
                    <div>
                      <span className="text-sm font-medium text-neutral-600">Challenges:</span>
                      <ul className="list-disc list-inside text-neutral-900 mt-2 space-y-1">
                        {companyData.strategicInitiatives.challenges.map((c, idx) => (
                          <li key={idx} className="text-sm">{c}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                  {companyData.strategicInitiatives.maActivity?.length ? (
                    <div>
                      <span className="text-sm font-medium text-neutral-600">M&A Activity:</span>
                      <ul className="list-disc list-inside text-neutral-900 mt-2 space-y-1">
                        {companyData.strategicInitiatives.maActivity.map((m, idx) => (
                          <li key={idx} className="text-sm">{m}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </div>
              </CardBody>
            </Card>
          )}

          {companyData.dataComplexity && (
            <Card>
              <CardHeader>
                <h3 className="text-lg font-semibold text-neutral-900">📊 Data Complexity</h3>
              </CardHeader>
              <CardBody>
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    {companyData.dataComplexity.scope && (
                      <div>
                        <span className="text-sm font-medium text-neutral-600">Scope:</span>
                        <p className="text-neutral-900">{companyData.dataComplexity.scope}</p>
                      </div>
                    )}
                    <div>
                      <span className="text-sm font-medium text-neutral-600">Multi-Channel:</span>
                      <p className="text-neutral-900">{companyData.dataComplexity.multiChannel ? 'Yes' : 'No'}</p>
                    </div>
                    {companyData.dataComplexity.productComplexity && (
                      <div>
                        <span className="text-sm font-medium text-neutral-600">Product Complexity:</span>
                        <p className="text-neutral-900">{companyData.dataComplexity.productComplexity}</p>
                      </div>
                    )}
                  </div>
                  {companyData.dataComplexity.recentAcquisitions?.length ? (
                    <div>
                      <span className="text-sm font-medium text-neutral-600">Recent Acquisitions:</span>
                      <ul className="list-disc list-inside text-neutral-900 mt-2 space-y-1">
                        {companyData.dataComplexity.recentAcquisitions.map((a, idx) => (
                          <li key={idx} className="text-sm">{a}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </div>
              </CardBody>
            </Card>
          )}

          {companyData.sources && companyData.sources.length > 0 && (
            <Card>
              <CardHeader>
                <h3 className="text-lg font-semibold text-neutral-900">📚 Research Sources</h3>
              </CardHeader>
              <CardBody>
                <ul className="space-y-2">
                  {companyData.sources.map((source, idx) => (
                    <li key={idx}>
                      <a
                        href={source}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-600 hover:text-primary-700 text-sm break-all"
                      >
                        {source}
                      </a>
                    </li>
                  ))}
                </ul>
              </CardBody>
            </Card>
          )}
        </div>
      ) : (
        <Card>
          <CardBody>
            <p className="text-center text-neutral-600">
              No company research data available for this project yet.
              <br />
              <span className="text-sm">
                Research and save a company to see detailed information here.
              </span>
            </p>
          </CardBody>
        </Card>
      )}

      <div className="mt-6">
        <LinkedResearchSection caseId={caseId} />
      </div>
    </div>
  );
}
